/**
 * Created by rathompson on 8/22/23
 * File: IconMenu.tsx
 * Project: cloud-mgmt-modules ©
 * ©
 */


import { useState, useEffect, useCallback, VFC, useMemo } from 'react'
import DotsButton from './DotsButton'
import {
  Popover,
  CardBody,
  List,
  ListItem,
  Body,
  Tooltip
} from '@panwds/react-ui'
import PopoverContent from '../../popover/PopoverContent'
import {
  ROOT_NODE_ID
} from '../../../../tenancy/constants/TenancyConstants'
import {
  LABEL_ADD_NEW_TENANT,
  LABEL_DELETE_TENANT
} from '../../../../tenancy/constants/LabelConstants'
import type { Tsg } from '@sparky/framework'
import { useIpRestrictionContext } from '../../../context/IpRestrictContext'
import _ from 'lodash'

export interface IconMenuItem {
  label: string,
  disabled: boolean,
  value: any,
  addClassName?: string,
  onItemClick?: (tenant: Tsg) => void
}

const IconMenu: VFC<{
  iconMenuList: IconMenuItem[],
  onContextClick?: (actionType: string, tenant: Tsg) => void,
  tenant: Tsg
}> = ({ iconMenuList, onContextClick, tenant }) => {

  const { isRestricted } = useIpRestrictionContext()

  const [isOpen, setIsOpen] = useState(false)
  const [shouldRender, setShouldRender] = useState(isOpen)
  const isRoot = tenant.id === ROOT_NODE_ID

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true)
    }
  }, [isOpen])

  const onItemClick = useCallback((item: IconMenuItem, tenant: Tsg) => {
    setIsOpen(false)
    if (item.onItemClick) {
      item.onItemClick(tenant)
    }
    else if (onContextClick) {
      onContextClick(item.value, tenant)
    }
  }, [onContextClick])

  const handleOpen = useCallback(() => setIsOpen(true), [])
  const handleOpenChange = useCallback((next) => setIsOpen(next), [])

  const list = useMemo(() => {
    return (
      <List style={{ width: '100%', padding: '0' }}>
        {iconMenuList.map((each, i) => {
          const { label, ...rest } = each
          rest.disabled = tenant.id === ROOT_NODE_ID && label === LABEL_ADD_NEW_TENANT ? false : rest.disabled || isRestricted || (isRoot && label !== LABEL_ADD_NEW_TENANT)
          const color = (label === LABEL_DELETE_TENANT ? 'cm-m-0 cm-text-red-700 cm-content-center' : 'cm-m-0 cm-text-gray-900 dark:cm-text-gray-100 cm-content-center')
          if (rest.disabled) {
            return (
              <ListItem
                style={{ width: '100%', opacity: 0.6 }}
                key={i}
                {..._.omit(rest, ['onItemClick'])}
                onClick={undefined}
              >
                <Body className={color} size='sm'>{label}</Body>
              </ListItem>
            )
          }
          return (
            <ListItem
              style={{ width: '100%' }}
              key={i}
              {..._.omit(rest, ['onItemClick'])}
              onClick={onItemClick.bind(this, each, tenant) as any}
            >
              <Body className={color} size='sm'>{label}</Body>
            </ListItem>
          )
        })}
      </List>
    )
  }, [iconMenuList, isRestricted, isRoot, onItemClick, tenant])

  if (!shouldRender) {
    return <DotsButton onClick={handleOpen} />
  }

  return (
    <div>
      <Popover
        defaultPlacement='bottom-start'
        isOpen={isOpen}
        onIsOpenChange={handleOpenChange}
        button={<DotsButton />}
        label={tenant?.id}
        showLabel={false}
      >
        <PopoverContent>
          <CardBody addClassName="cm-flex cm-flex-col cm-px-0 cm-py-0 cm-m-0">
            {tenant.id !== ROOT_NODE_ID && isRestricted ? (
              <Tooltip
                defaultPlacement='right-start'
                label={'Tenant is IP restricted'}
              >
                {list}
              </Tooltip>
            ) : list }
          </CardBody>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default IconMenu
